import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import PrintPage from './print/PrintPage';
import { captureMap } from './print/capture';
import { PREVIEW_WRAPPER_ATTR, exportPdf, exportPng, pageDataUrl } from './print/export';
import {
  DPI_CHOICES, LEGEND_PLACEMENTS, PAGE_SIZES, composePage, pageDimensions,
} from '../lib/print-layout';
import { setReportHandoff } from '../lib/report-handoff';

/**
 * MapLayout — print / export page composer (Wave 5.4).
 *
 * Captures the live map (after `idle`, canvas created with preserveDrawingBuffer) at the chosen
 * scale and composes a page: title, subtitle, map, scale bar, north arrow (follows the bearing),
 * legend (layer swatches + colour scales), graticule, date and CRS/datum text, optional logo.
 * Exports PNG / PDF at 150 or 300 dpi, or hands the page to the Report Builder.
 *
 * Props:
 *   mapRefHolder  - MapView's ref holder ({ current: <react-map-gl MapRef> })
 *   layers        - [{ id, name, visible, color }] for the swatch legend
 *   colorScales   - [{ title, min, max, colors }] for the colour ramps
 *   projectName, crsLabel
 *   onSendToReport() - called after the page was handed to the Report Builder
 *   onClose()
 */
export default function MapLayout({ mapRefHolder, layers = [], colorScales = [], projectName = 'Ultimate-GIS Project', crsLabel = '', onSendToReport, onClose }) {
  const { C } = useTheme();
  const pageRef = useRef(null);
  const [size, setSize] = useState('a4');
  const [orientation, setOrientation] = useState('landscape');
  const [title, setTitle] = useState(projectName);
  const [subtitle, setSubtitle] = useState('');
  const [units, setUnits] = useState('metric');
  const [scaleMode, setScaleMode] = useState('view'); // 'view' | 'fixed'
  const [denominator, setDenominator] = useState(5000);
  const [showLegend, setShowLegend] = useState(true);
  const [legendPlacement, setLegendPlacement] = useState('bottom-right');
  const [showNorth, setShowNorth] = useState(true);
  const [showScale, setShowScale] = useState(true);
  const [showGraticule, setShowGraticule] = useState(false);
  const [dpi, setDpi] = useState(150);
  const [logo, setLogo] = useState(null); // session only, never stored
  const [capture, setCapture] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fit, setFit] = useState(0.7);
  const dateText = useMemo(() => `Printed ${new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}`, []);

  const page = useMemo(() => pageDimensions(size, orientation), [size, orientation]);
  const frame = useMemo(() => composePage(page.widthPx, page.heightPx, legendPlacement, showLegend).map, [page, legendPlacement, showLegend]);
  const fixedN = scaleMode === 'fixed' && denominator >= 1 ? Math.round(denominator) : null;

  // Re-capture when the frame or the requested scale changes (debounced: it moves the camera briefly)
  const seq = useRef(0);
  const doCapture = useCallback(async () => {
    const map = mapRefHolder?.current?.current?.getMap?.();
    if (!map) { setError('The map is not ready yet.'); return; }
    const mine = ++seq.current;
    setBusy(true); setError('');
    try {
      const res = await captureMap(map, { denominator: fixedN, frame });
      if (mine === seq.current) setCapture(res);
    } catch (e) {
      if (mine === seq.current) setError(`Could not capture the map: ${e?.message || e}`);
    } finally {
      if (mine === seq.current) setBusy(false);
    }
  }, [mapRefHolder, fixedN, frame]);

  useEffect(() => {
    const t = setTimeout(doCapture, 300);
    return () => clearTimeout(t);
  }, [doCapture]);

  // Fit the preview to the window width
  useEffect(() => {
    const fitNow = () => setFit(Math.max(0.3, Math.min(1, (window.innerWidth - 360) / page.widthPx)));
    fitNow();
    window.addEventListener('resize', fitNow);
    return () => window.removeEventListener('resize', fitNow);
  }, [page.widthPx]);

  const opts = { title, subtitle, units, legendPlacement, showLegend, showNorth, showScale, showGraticule, dateText, projectName };

  const run = (fn) => async () => {
    if (!pageRef.current || !capture) return;
    setBusy(true); setError('');
    try { await fn(pageRef.current); } catch (e) { setError(`Export failed: ${e?.message || e}`); } finally { setBusy(false); }
  };
  const onPng = run((node) => exportPng(node, { dpi, title }));
  const onPdf = run((node) => exportPdf(node, { dpi, title, size, orientation }));
  const onSend = run(async (node) => {
    setReportHandoff({ dataUrl: await pageDataUrl(node, 150), pageSize: size, orientation, title });
    if (onSendToReport) onSendToReport(); else onClose?.();
  });

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setLogo(ev.target.result);
    reader.readAsDataURL(file);
  };

  const field = { fontSize: 12, padding: '5px 8px', borderRadius: 6, background: C.bg3, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', width: '100%', boxSizing: 'border-box' };
  const label = { fontSize: 10, color: C.text2, textTransform: 'uppercase', display: 'flex', flexDirection: 'column', gap: 3 };
  const check = { fontSize: 12, color: C.text1, display: 'flex', alignItems: 'center', gap: 6 };
  const btn = (primary) => ({
    fontSize: 12, padding: '7px 12px', borderRadius: 6, cursor: busy || !capture ? 'default' : 'pointer', opacity: busy || !capture ? 0.6 : 1,
    ...(primary ? { background: C.cyan, color: '#0b1220', border: 'none', fontWeight: 600 } : { background: C.bg3, color: C.text1, border: `1px solid ${C.border}` }),
  });

  return (
    <div role="dialog" aria-label="Print layout" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex' }}>
      {/* Controls */}
      <div style={{ width: 300, flexShrink: 0, background: C.bg1, borderRight: `1px solid ${C.border}`, padding: 14, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text0 }}>Print / Export map</div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ fontSize: 16, background: 'none', border: 'none', color: C.text3, cursor: 'pointer' }}>{'✕'}</button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <label style={{ ...label, flex: 1 }}>Page size
            <select aria-label="Page size" value={size} onChange={(e) => setSize(e.target.value)} style={field}>
              {Object.entries(PAGE_SIZES).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
            </select>
          </label>
          <label style={{ ...label, flex: 1 }}>Orientation
            <select aria-label="Orientation" value={orientation} onChange={(e) => setOrientation(e.target.value)} style={field}>
              <option value="landscape">Landscape</option>
              <option value="portrait">Portrait</option>
            </select>
          </label>
        </div>
        <label style={label}>Title<input aria-label="Title" value={title} onChange={(e) => setTitle(e.target.value)} style={field} /></label>
        <label style={label}>Subtitle<input aria-label="Subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Survey, date range, notes" style={field} /></label>

        <div style={{ display: 'flex', gap: 8 }}>
          <label style={{ ...label, flex: 1 }}>Map scale
            <select aria-label="Map scale" value={scaleMode} onChange={(e) => setScaleMode(e.target.value)} style={field}>
              <option value="view">Current view</option>
              <option value="fixed">Fixed scale 1:</option>
            </select>
          </label>
          {scaleMode === 'fixed' && (
            <label style={{ ...label, flex: 1 }}>1 :
              <input aria-label="Scale denominator" type="number" min="1" step="100" value={denominator} onChange={(e) => setDenominator(Number(e.target.value))} style={field} />
            </label>
          )}
        </div>
        <label style={label}>Scale bar units
          <select aria-label="Scale bar units" value={units} onChange={(e) => setUnits(e.target.value)} style={field}>
            <option value="metric">Metres / kilometres</option>
            <option value="imperial">Feet / miles</option>
          </select>
        </label>

        <label style={check}><input type="checkbox" checked={showNorth} onChange={(e) => setShowNorth(e.target.checked)} /> North arrow</label>
        <label style={check}><input type="checkbox" checked={showScale} onChange={(e) => setShowScale(e.target.checked)} /> Scale bar</label>
        <label style={check}><input type="checkbox" checked={showGraticule} onChange={(e) => setShowGraticule(e.target.checked)} /> Coordinate graticule (lon/lat)</label>
        <label style={check}><input type="checkbox" checked={showLegend} onChange={(e) => setShowLegend(e.target.checked)} /> Legend</label>
        {showLegend && (
          <label style={label}>Legend placement
            <select aria-label="Legend placement" value={legendPlacement} onChange={(e) => setLegendPlacement(e.target.value)} style={field}>
              {LEGEND_PLACEMENTS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>
        )}
        <label style={label}>Logo (this session only)
          <input aria-label="Logo" type="file" accept="image/*" onChange={onLogo} style={{ ...field, padding: 3 }} />
        </label>
        {logo && <button type="button" onClick={() => setLogo(null)} style={{ ...btn(false), alignSelf: 'flex-start' }}>Remove logo</button>}

        <label style={label}>Export resolution
          <select aria-label="Export resolution" value={dpi} onChange={(e) => setDpi(Number(e.target.value))} style={field}>
            {DPI_CHOICES.map((d) => <option key={d} value={d}>{d} dpi</option>)}
          </select>
        </label>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
          <button type="button" onClick={onPng} disabled={busy || !capture} style={btn(true)}>Export PNG</button>
          <button type="button" onClick={onPdf} disabled={busy || !capture} style={btn(false)}>Export PDF</button>
          <button type="button" onClick={onSend} disabled={busy || !capture} style={btn(false)}>Send to Report Builder</button>
          <button type="button" onClick={doCapture} disabled={busy} style={btn(false)}>Re-capture map</button>
        </div>
        {busy && <div style={{ fontSize: 11, color: C.text2 }} role="status">Working…</div>}
        {error && <div style={{ fontSize: 11, color: '#f87171' }} role="alert">{error}</div>}
        <div style={{ fontSize: 10, color: C.text2 }}>
          The map is captured from the live view after it finishes drawing. A fixed scale briefly moves the camera and restores it. Scale is true at the full printed page size.
        </div>
      </div>

      {/* Preview */}
      <div style={{ flex: 1, overflow: 'auto', padding: 20, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
        <div style={{ width: page.widthPx * fit, height: page.heightPx * fit, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', flexShrink: 0 }}>
          <div {...{ [PREVIEW_WRAPPER_ATTR]: '' }} style={{ transform: `scale(${fit})`, transformOrigin: 'top left', width: page.widthPx, height: page.heightPx }}>
            <PrintPage ref={pageRef} page={page} opts={opts} capture={capture} layers={layers.filter((l) => l.visible)} colorScales={colorScales} logo={logo} crsLabel={crsLabel} />
          </div>
        </div>
      </div>
    </div>
  );
}
